import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Sparkles, Heart, Users, Star, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function AboutPage() {
  const { t } = useLanguage();

  const categoryCards = [
    { icon: Heart, titleKey: 'categories.restaurang', descKey: 'about.catDescRestaurang', image: "/cat-restaurang.png" },
    { icon: Sparkles, titleKey: 'categories.wellness', descKey: 'about.catDescWellness', image: "/cat-wellness.jpg" },
    { icon: CheckCircle, titleKey: 'categories.traning', descKey: 'about.catDescTranning', image: "/cat-traning.png" },
    { icon: Star, titleKey: 'categories.kultur', descKey: 'about.catDescKultur', image: "/cat-kultur.png" },
    { icon: Users, titleKey: 'categories.hotell', descKey: 'about.catDescHotell', image: "/cat-hotell.png" },
    { icon: Sparkles, titleKey: 'categories.hem', descKey: 'about.catDescHem', image: "/cat-hem.png" }
  ];

  return (
    <div className="min-h-screen bg-[#F4EDE6]" data-testid="about-page">
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <video 
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
          src="/about-video.mp4"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="container-main text-center text-white relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-['Avenir_Next'] text-4xl md:text-6xl mb-6">
              {t('about.heroTitle')}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {t('about.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="prose prose-lg"
            >
              <p className="text-xl text-[#595959] leading-relaxed mb-8">
                {t('about.intro')}
              </p>

              <h2 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mt-12 mb-6">
                {t('about.visionTitle')}
              </h2>
              <p className="text-[#595959] leading-relaxed mb-6">
                {t('about.visionText')}
              </p>

              <div className="text-[#595959] leading-relaxed mb-8" data-testid="vision-examples">
                <p className="mb-3">{t('about.visionMonth1')}</p>
                <ul className="list-disc list-inside ml-2 mb-5 space-y-1">
                  {(t('about.visionList1') || []).map((item, i) => <li key={i}>{item}</li>)}
                </ul>
                <p className="mb-3">{t('about.visionMonth2')}</p>
                <ul className="list-disc list-inside ml-2 mb-5 space-y-1">
                  {(t('about.visionList2') || []).map((item, i) => <li key={i}>{item}</li>)}
                </ul>
                <p>{t('about.visionConclusion')}</p>
              </div>

              <h2 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mt-12 mb-6">
                {t('about.howTitle')}
              </h2>
              <p className="text-[#595959] leading-relaxed mb-4">
                {t('about.howDesc')}
              </p>
            </motion.div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 my-12">
              {categoryCards.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="relative rounded-2xl overflow-hidden h-48 group"
                >
                  <img src={item.image} alt={t(item.titleKey)} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors" />
                  <div className="relative z-10 h-full flex flex-col items-center justify-center text-center p-6">
                    <div className="w-14 h-14 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                      <item.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-medium text-white mb-2">{t(item.titleKey)}</h3>
                    <p className="text-sm text-white/70">{t(item.descKey)}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mt-12 mb-6">
                {t('about.whyTitle')}
              </h2>
              <div className="space-y-4 mb-12">
                {(t('about.whyList') || []).map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-[#595959]">
                    <CheckCircle className="w-5 h-5 text-[#C4A77D] flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <h2 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mt-12 mb-6">
                {t('about.forAllTitle')}
              </h2>
              <p className="text-[#595959] leading-relaxed mb-8">
                {t('about.forAllText')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden">
        <video 
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
          src="/about-cta-video.mp4"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="container-main text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Avenir_Next'] text-3xl md:text-4xl text-white mb-6">
              {t('about.ctaTitle')}
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              {t('about.ctaDesc')}
            </p>
            <Link to="/bli-medlem">
              <Button className="bg-[#C4A77D] hover:bg-[#B39669] text-white h-14 px-8 text-base rounded-full">
                {t('about.ctaBtn')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
